import { Eye, EyeOff, Loader2 } from "lucide-react";
import React, { useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { toast } from "sonner";

import { toastError, toastWarning } from "../../../../utils/showApiErrorToast";
import {
  BROKER_ALERT_TITLE_CLASS,
  BROKER_FIELD_DESC_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import SettingCard, {
  accountSettingsFieldClassName,
  accountSettingsFieldRowClassName,
  accountSettingsFieldStackClassName,
} from "./SettingCard";

type PasswordFieldInputProps = {
  id: string;
  value: string;
  placeholder: string;
  revealed: boolean;
  invalid?: boolean;
  onChange: (value: string) => void;
  onToggleReveal: () => void;
};

function PasswordFieldInput({
  id,
  value,
  placeholder,
  revealed,
  invalid = false,
  onChange,
  onToggleReveal,
}: PasswordFieldInputProps): React.ReactElement {
  return (
    <InputGroup className="h-9 w-full font-[family-name:var(--font-primary)]">
      <InputGroupInput
        id={id}
        type={revealed ? "text" : "password"}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        data-font="primary"
        className="text-sm normal-case tracking-normal"
        autoComplete="off"
      />
      <InputGroupAddon align="inline-end" className="pr-1">
        <InputGroupButton
          type="button"
          size="icon-xs"
          variant="ghost"
          aria-label={revealed ? "Hide password" : "Show password"}
          aria-pressed={revealed}
          onClick={onToggleReveal}
          className="text-muted-foreground hover:text-muted-foreground"
        >
          {revealed ? <Eye aria-hidden /> : <EyeOff aria-hidden />}
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  );
}

const PasswordSetting: React.FC = () => {
  const { user } = useUser();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [errors, setErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  const handleCancel = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowUpdateForm(false);
    setErrors({});
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return "Password is required";
    }
    if (password.length < 8) {
      return "Password must be at least 8 characters";
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!currentPassword) {
      newErrors.currentPassword = "Current password is required";
    }

    const newPasswordError = validatePassword(newPassword);
    if (newPasswordError) {
      newErrors.newPassword = newPasswordError;
    }

    if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (currentPassword === newPassword) {
      newErrors.newPassword = "New password must be different from current password";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdatePassword = async () => {
    if (!user) return;

    if (!validateForm()) {
      return;
    }

    setIsUpdating(true);

    try {
      await user.updatePassword({
        currentPassword,
        newPassword,
        signOutOfOtherSessions: false,
      });

      toast.success("Password updated", { description: "Your password has been updated successfully." });

      handleCancel();
    } catch (error: unknown) {
      console.error("Error updating password:", error);
      const err = error as { errors?: Array<{ code?: string; message?: string }>; message?: string };
      const clerkError = err?.errors?.[0];
      const errorCode = clerkError?.code || "";
      const rawMessage = clerkError?.message || err?.message || "Failed to update password";
      const errorMessage = String(rawMessage);

      if (
        errorCode === "session_verification_required" ||
        errorCode === "verification_required" ||
        errorMessage.toLowerCase().includes("verification required")
      ) {
        toastWarning("Re‑authentication required", {
          description: "For security, please sign out and sign back in, then update your password again.",
        });
      } else if (
        errorMessage.toLowerCase().includes("current password") ||
        errorMessage.toLowerCase().includes("incorrect")
      ) {
        setErrors({ currentPassword: "Current password is incorrect" });
      } else {
        toastError("Update failed", { description: errorMessage });
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <SettingCard
      title="Password"
      description="Change your account password. Make sure to use a strong password."
    >
      {!showUpdateForm ? (
        <div className={accountSettingsFieldRowClassName}>
          <Input
            id="password-masked"
            type="password"
            value="••••••••••••••••"
            readOnly
            className="min-w-0 flex-1 cursor-not-allowed bg-muted/50"
            data-font="primary"
          />
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="shrink-0"
            onClick={() => setShowUpdateForm(true)}
          >
            Update
          </Button>
        </div>
      ) : (
        <div className={accountSettingsFieldStackClassName}>
          <Field className={cn(accountSettingsFieldClassName, "mb-4")} data-invalid={!!errors.currentPassword || undefined}>
            <FieldLabel htmlFor="current-password" className={BROKER_ALERT_TITLE_CLASS}>
              Current password
            </FieldLabel>
            <PasswordFieldInput
              id="current-password"
              value={currentPassword}
              placeholder="Enter your current password"
              revealed={showCurrentPassword}
              invalid={!!errors.currentPassword}
              onChange={(value) => {
                setCurrentPassword(value);
                if (errors.currentPassword) {
                  setErrors({ ...errors, currentPassword: undefined });
                }
              }}
              onToggleReveal={() => setShowCurrentPassword((prev) => !prev)}
            />
            {errors.currentPassword ? <FieldError>{errors.currentPassword}</FieldError> : null}
          </Field>

          <Field className={cn(accountSettingsFieldClassName, "mb-4")} data-invalid={!!errors.newPassword || undefined}>
            <FieldLabel htmlFor="new-password" className={BROKER_ALERT_TITLE_CLASS}>
              New password
            </FieldLabel>
            <PasswordFieldInput
              id="new-password"
              value={newPassword}
              placeholder="Enter your new password"
              revealed={showNewPassword}
              invalid={!!errors.newPassword}
              onChange={(value) => {
                setNewPassword(value);
                if (errors.newPassword) {
                  setErrors({ ...errors, newPassword: undefined });
                }
                if (errors.confirmPassword && value === confirmPassword) {
                  setErrors({ ...errors, confirmPassword: undefined });
                }
              }}
              onToggleReveal={() => setShowNewPassword((prev) => !prev)}
            />
            {errors.newPassword ? <FieldError>{errors.newPassword}</FieldError> : null}
            <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
              Password must be at least 8 characters long.
            </FieldDescription>
          </Field>

          <Field className={cn(accountSettingsFieldClassName, "mb-4")} data-invalid={!!errors.confirmPassword || undefined}>
            <FieldLabel htmlFor="confirm-password" className={BROKER_ALERT_TITLE_CLASS}>
              Confirm new password
            </FieldLabel>
            <PasswordFieldInput
              id="confirm-password"
              value={confirmPassword}
              placeholder="Confirm your new password"
              revealed={showConfirmPassword}
              invalid={!!errors.confirmPassword}
              onChange={(value) => {
                setConfirmPassword(value);
                if (errors.confirmPassword) {
                  setErrors({ ...errors, confirmPassword: undefined });
                }
              }}
              onToggleReveal={() => setShowConfirmPassword((prev) => !prev)}
            />
            {errors.confirmPassword ? <FieldError>{errors.confirmPassword}</FieldError> : null}
          </Field>

          <div className="mt-4 flex flex-row gap-2">
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={isUpdating}
              aria-busy={isUpdating || undefined}
              onClick={() => void handleUpdatePassword()}
            >
              {isUpdating ? (
                <Loader2 data-icon="inline-start" className="size-3.5 animate-spin" aria-hidden />
              ) : null}
              Update
            </Button>
            <Button
              type="button"
              variant="outline-red"
              size="sm"
              data-font="primary"
              className="shrink-0"
              disabled={isUpdating}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </SettingCard>
  );
};

export default PasswordSetting;
